import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Container, Row, Button, Badge } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import Pagination from "../components/Pagination";
import SimpleHeader from "components/Headers/SimpleHeader";
import userServices from "services/userServices";
import { useNotification } from "providers/NotificationProvider";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";
import IpStatusReasonModal from "./IpStatusReasonModal";
import ManualBlockModal from "./ManualBlockModal";
import { useAuth } from "context/AuthContext";
import useNotificationHighlight from "hooks/useNotificationHighlight";

const FILTER_TABS = [
  { key: "all",      labelKey: "common.all", defaultText: "All" },
  { key: "active",   labelKey: "page.blockedIps.tabs.active", defaultText: "Active" },
  { key: "inactive", labelKey: "page.blockedIps.tabs.inactive", defaultText: "Expired / Inactive" },
  { key: "system",   labelKey: "page.blockedIps.tabs.system", defaultText: "System Blocks", source: "SYSTEM" },
  { key: "admin",    labelKey: "page.blockedIps.tabs.admin", defaultText: "Admin Blocks", source: "ADMIN" },
];

const BlockedIpsTable = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { notify } = useNotification();
  const { isHighlighted } = useNotificationHighlight();
  const componentRef = useRef(null);

  const tRef = useRef(t);
  tRef.current = t;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const { permission } = useAuth();
  const blockedIpsPermission = permission?.permissions?.modules?.failedLogin;

  const [activeTab, setActiveTab] = useState("all");

  const [tableState, setTableState] = useState({
    page: 1, limit: 20, search: "", sortBy: "createdAt", sortOrder: -1,
  });
  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 0 });
  const [localSearch, setLocalSearch] = useState(tableState.search || "");

  useEffect(() => {
    const handler = setTimeout(() => {
      setTableState((prev) => {
        const prevSearch = prev.search || "";
        if (prevSearch === localSearch) {
          return prev;
        }
        return {
          ...prev,
          search: localSearch,
          page: 1,
        };
      });
    }, 400);
    return () => clearTimeout(handler);
  }, [localSearch]);

  // Modals
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [blockModalOpen,  setBlockModalOpen]  = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchIps = useCallback(async () => {
    try {
      setLoading(true);

      const tab = FILTER_TABS.find(t => t.key === activeTab) || {};
      const params = {
        page:      tableState.page,
        limit:     tableState.limit,
        search:    tableState.search,
        sortBy:    tableState.sortBy,
        sortOrder: tableState.sortOrder,
        status:    ["active", "inactive"].includes(activeTab) ? activeTab : "all",
        source:    tab.source || undefined,
      };

      const res = await userServices.getBlockedIps(params);
      if (res?.error) {
        notifyRef.current("error", tRef.current("page.blockedIps.fetchFailed", "Failed to fetch blocked IPs"));
        return;
      }

      const resData = res;
      const dataObj = resData?.success && resData?.data !== undefined ? resData.data : resData;
      setIps(Array.isArray(dataObj) ? dataObj : (dataObj?.data || []));
      setMeta(resData?.meta || dataObj?.meta || { totalItems: 0, totalPages: 0 });
    } catch {
      notifyRef.current("error", tRef.current("page.users.toast.somethingWentWrong", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  }, [tableState.page, tableState.limit, tableState.search, tableState.sortBy, tableState.sortOrder, activeTab]);

  useEffect(() => {
    fetchIps();
  }, [fetchIps]);

  const openStatusModal  = (row) => { setSelectedRow(row); setStatusModalOpen(true); };
  const openBlockModal   = (row) => { setSelectedRow(row); setBlockModalOpen(true); };

  const handleStatusConfirm = async (data) => {
    try {
      const res = await userServices.updateIPStatus(data);
      if (res?.success) {
        notify("success", t(res?.messageKey || "page.blockedIps.updateSuccess", res?.message || "IP status updated"));
        fetchIps();
        setStatusModalOpen(false);
        setSelectedRow(null);
      } else {
        notify("error", t(res?.error || "common.failed", res?.message || "Failed"));
      }
    } catch {
      notify("error", t("page.users.toast.somethingWentWrong", "Something went wrong"));
    }
  };

  const handleBlockConfirm = async (data) => {
    try {
      const res = await userServices.updateIPStatus({ ...data, isActive: true });
      if (res?.success) {
        notify("success", t("page.blockedIps.blockSuccess", "IP blocked successfully"));
        fetchIps();
        setBlockModalOpen(false);
        setSelectedRow(null);
      } else {
        notify("error", t(res?.error || "common.failed", res?.message || "Failed"));
      }
    } catch {
      notify("error", t("page.users.toast.somethingWentWrong", "Something went wrong"));
    }
  };

  const columns = useMemo(
    () => [
      {
        dataField: "ip_address",
        text: t("table.headers.ipAddress", "IP Address"),
        sort: true,
        headerAlign: "left",
        align: "left",
        style: { whiteSpace: "nowrap" },
      },

      {
        dataField: "attempted_email",
        text: t("table.headers.userAccount", "User / Email"),
        sort: true,
        headerAlign: "left",
        align: "left",
        formatter: (_, row) => {
          const hasMatchedUser = !!row?.matched_user?.name;
          const email = row?.attempted_email && row.attempted_email !== "-" ? row.attempted_email : null;

          return (
            <div className="d-flex flex-column align-items-start line-height-1-3">
              {hasMatchedUser ? (
                <span className="text-sm font-weight-500 text-dark">
                  {row.matched_user.name}
                </span>
              ) : (
                <Badge className="badge-pill px-2 py-0.5 text-uppercase mb-0.5 badge-no-account">
                  {t("page.blockedIps.noMatchingAccount", "No matching account")}
                </Badge>
              )}
              {email && (
                <span className="text-muted text-break font-size-11">
                  {email}
                </span>
              )}
            </div>
          );
        },
      },
      {
        dataField: "blockType",
        text: t("table.headers.blockTypeAndDuration", "Block Type & Duration"),
        headerAlign: "left",
        align: "left",
        formatter: (_, row) => {
          if (!row?.isActive) {
            return (
              <Badge className="badge-pill px-2.5 py-1 text-uppercase badge-status-unblocked">
                {t("page.blockedIps.status.unblocked", "Unblocked")}
              </Badge>
            );
          }

          const isPerm = row.blockType === "PERMANENT" || !row.blockedUntil;
          if (isPerm) {
            return (
              <Badge className="badge-pill px-2.5 py-1 text-uppercase badge-status-permanent">
                {t("page.blockedIps.status.permanent", "Permanent")}
              </Badge>
            );
          }

          const now = Date.now();
          const until = new Date(row.blockedUntil).getTime();
          const remainingMs = until - now;

          if (remainingMs <= 0) {
            return (
              <Badge className="badge-pill px-2.5 py-1 text-uppercase badge-status-expired">
                {t("page.blockedIps.status.expired", "Expired")}
              </Badge>
            );
          }

          const totalDurationMs = row.blockedAt
            ? until - new Date(row.blockedAt).getTime()
            : 0;

          const remainingMin = Math.max(1, Math.ceil(remainingMs / 60000));
          const totalMin = totalDurationMs > 0 ? Math.round(totalDurationMs / 60000) : 0;

          const remainingText = helpers.formatRemainingTime(remainingMs, t);

          let durationLabel = "";
          if (totalMin > 0) {
            if (totalMin >= 14 && totalMin <= 16) durationLabel = t("page.blockedIps.durations.15m", "15 Min");
            else if (totalMin >= 115 && totalMin <= 125) durationLabel = t("page.blockedIps.durations.2h", "2 Hours");
            else if (totalMin >= 1400 && totalMin <= 1500) durationLabel = t("page.blockedIps.durations.24h", "24 Hours");
            else if (totalMin >= 8000) durationLabel = t("page.blockedIps.durations.7d", "7 Days");
            else if (totalMin >= 60) durationLabel = `${Math.round(totalMin / 60)}h`;
            else durationLabel = `${totalMin}m`;
          } else {
            durationLabel = remainingMin <= 15
              ? t("page.blockedIps.durations.15m", "15 Min")
              : remainingMin <= 120
              ? t("page.blockedIps.durations.2h", "2 Hours")
              : remainingMin <= 1440
              ? t("page.blockedIps.durations.24h", "24 Hours")
              : t("page.blockedIps.durations.7d", "7 Days");
          }

          return (
            <div className="d-flex flex-column align-items-start line-height-1-3">
              <Badge className="badge-pill px-2.5 py-1 text-uppercase mb-1 badge-status-temporary">
                {t("page.blockedIps.status.temporary", "Temp")} ({durationLabel})
              </Badge>
              <span className="text-muted text-nowrap font-size-11 font-weight-500">
                {remainingText}
              </span>
            </div>
          );
        },
      },
      {
        dataField: "source",
        text: t("table.headers.source", "Source"),
        headerAlign: "left",
        align: "left",
        formatter: (cell) => {
          const isAdmin = cell === "ADMIN";
          return (
            <Badge className={`badge-pill px-2.5 py-1 text-uppercase ${isAdmin ? "badge-source-admin" : "badge-source-system"}`}>
              {isAdmin ? t("page.blockedIps.sources.admin", "Admin") : t("page.blockedIps.sources.system", "System")}
            </Badge>
          );
        },
      },
      {
        dataField: "totalAttempts",
        text: t("table.headers.totalAttempts", "Total Attempts"),
        sort: true,
        headerAlign: "left",
        align: "left",
        formatter: (cell) => cell || 0,
      },
      {
        dataField: "affectedAccountsCount",
        text: t("table.headers.affectedAccounts", "Accounts"),
        headerAlign: "left",
        align: "left",
        formatter: (cell) => (cell != null && cell > 0) ? cell : <span className="text-muted">N/A</span>,
      },
      {
        dataField: "actions",
        text: t("table.headers.actions", "Actions"),
        headerAlign: "left",
        align: "left",
        formatter: (_, row) => {
          const canUpdate = blockedIpsPermission?.update;
          return (
            <div className="d-flex gap-1 align-items-center">
              {canUpdate && row.isActive && (
                <Button size="sm" color="success" className="py-1 px-2.5" onClick={() => openStatusModal(row)}>
                  {t("page.blockedIps.actions.unblock", "Unblock")}
                </Button>
              )}
              {canUpdate && !row.isActive && (
                <Button size="sm" color="danger" className="py-1 px-2.5" onClick={() => openBlockModal(row)}>
                  {t("page.blockedIps.actions.block", "Block")}
                </Button>
              )}
              <Button
                size="sm"
                color="secondary"
                outline
                className="py-1 px-2.5"
                onClick={() => navigate(`/admin/failedLogin/details/${encodeURIComponent(row.ip_address)}`)}
              >
                {t("page.blockedIps.actions.history", "History")}
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [t, language, blockedIpsPermission, navigate]
  );

  const handleTableChange = (type, { page: changePage, sizePerPage, sortField, sortOrder }) => {
    if (type === "sort" && sortField) {
      const newOrder = sortOrder === "asc" ? 1 : -1;
      setTableState((prev) => {
        if (prev.sortBy === sortField && prev.sortOrder === newOrder) {
          return prev;
        }
        return {
          ...prev,
          sortBy: sortField,
          sortOrder: newOrder,
          page: 1,
        };
      });
    } else if (type === "pagination" && changePage) {
      setTableState((prev) => ({
        ...prev,
        page: changePage,
        limit: sizePerPage || prev.limit,
      }));
    }
  };

  const rowEvents = useMemo(
    () => ({
      onClick: (e, row) => {
        const target = e.target;
        if (
          target.closest("button") ||
          target.closest(".dropdown") ||
          target.closest(".dropdown-menu") ||
          target.closest(".dropdown-toggle") ||
          target.closest("a") ||
          target.closest("input")
        ) {
          return;
        }
        if (row?.ip_address && blockedIpsPermission?.read) {
          navigate(`/admin/failedLogin/details/${encodeURIComponent(row.ip_address)}`);
        }
      },
    }),
    [blockedIpsPermission?.read, navigate]
  );

  return (
    <div className="card-header-full-height">
      <SimpleHeader
        name={t("sideNav.failedLogin", "Failed Login")}
        parentName={t("page.blockedIps.title", "Blocked IPs")}
      />

      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="shadow" ref={componentRef}>
              <ToolkitProvider key={`blocked-ips-${language}`} keyField="_id" data={ips} columns={columns} search={false}>
                {(props) => (
                  <div className="py-3 flex flex-col">
                    {/* Filter Tabs */}
                    <div className="px-4 pb-2 d-flex align-items-center gap-2 flex-wrap border-bottom">
                      {FILTER_TABS.map((tab) => (
                        <button
                          key={tab.key}
                          className={`btn btn-sm ${activeTab === tab.key ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => {
                            setActiveTab(tab.key);
                            setTableState((p) => ({ ...p, page: 1 }));
                          }}
                        >
                          {t(tab.labelKey, tab.defaultText)}
                        </button>
                      ))}
                    </div>

                    <div
                      id="datatable-basic_filter"
                      className="dataTables_filter px-4 pb-3 pt-3 d-flex align-items-center justify-content-between w-100 flex-wrap gap-2"
                    >
                      <label className="d-flex align-items-center gap-2 mb-0">
                        <span className="text-sm font-medium text-slate-600">
                          {t("table.search", "Search")}:
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={t("table.search", "Search")}
                          value={localSearch}
                          onChange={(e) => setLocalSearch(e.target.value)}
                        />
                      </label>
                      {blockedIpsPermission?.update && (
                        <Button size="sm" color="danger" onClick={() => { setSelectedRow(null); setBlockModalOpen(true); }}>
                          + {t("page.blockedIps.actions.blockNewIp", "Block IP")}
                        </Button>
                      )}
                    </div>

                    <div className="table-responsive">
                      <BootstrapTable
                        key={`table-${language}`}
                        {...props.baseProps}
                        columns={columns}
                        bootstrap4
                        bordered={false}
                        keyField="_id"
                        rowClasses={(row) => {
                          const isRowHighlighted =
                            isHighlighted("ip_block", row?.ip_address) ||
                            isHighlighted("ip_block", row?._id) ||
                            isHighlighted("security", row?.ip_address) ||
                            isHighlighted("security", row?._id) ||
                            isHighlighted("failed_login", row?.ip_address);
                          return `cursor-pointer ${isRowHighlighted ? "bg-[#F0F7FF] border-left-highlight" : ""}`.trim();
                        }}
                        rowEvents={rowEvents}
                        remote={{ sort: true, pagination: true }}
                        classes="table align-items-center table-flush"
                        headerClasses="thead-light"
                        onTableChange={handleTableChange}
                        defaultSorted={[
                          {
                            dataField: tableState.sortBy || "createdAt",
                            order: tableState.sortOrder === 1 ? "asc" : "desc",
                          },
                        ]}
                        noDataIndication={() => (
                          <div className="text-center py-4 text-muted">
                            {loading ? t("common.loading", "Loading....") : t("page.blockedIps.noData", "No blocked IPs")}
                          </div>
                        )}
                      />
                    </div>
                    <Pagination
                      totalRows={meta.totalItems || 0}
                      rowsPerPage={tableState.limit}
                      currentPage={tableState.page - 1}
                      onPageChange={(p) => setTableState((prev) => ({ ...prev, page: p + 1 }))}
                      onRowsPerPageChange={(size) => setTableState((prev) => ({ ...prev, limit: size, page: 1 }))}
                      t={t}
                    />
                  </div>
                )}
              </ToolkitProvider>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Unblock modal */}
      <IpStatusReasonModal
        isOpen={statusModalOpen}
        toggle={() => setStatusModalOpen(false)}
        selectedRow={selectedRow}
        onConfirm={handleStatusConfirm}
      />

      {/* Manual block modal */}
      <ManualBlockModal
        isOpen={blockModalOpen}
        toggle={() => setBlockModalOpen(false)}
        selectedRow={selectedRow}
        onConfirm={handleBlockConfirm}
      />
    </div>
  );
};

export default BlockedIpsTable;
